import React, { FC, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Mousewheel } from "swiper/modules";
import "swiper/css";
import "swiper/css/mousewheel";
import {
  Benefits_benefitItemsCollection_items,
} from "../../../../__generated__/graphql";
import TextFieldsV2 from "../TextFieldsV2";
import classnames from "classnames";
import { ReviewCard, CarouselControls } from "ui-kit";
import { useWebTranslation } from "translations";
import { isOrg } from "utils/site";
import styles from "./ReviewsCarousel.module.css";

const DISCLAIMER_KEY = "benefits.reviews.disclaimer";

type ReviewsCarouselProps = {
  title?: string | null;
  text?: string | null;
  items: Benefits_benefitItemsCollection_items[];
};

const ReviewsCarousel: FC<ReviewsCarouselProps> = ({ title, text, items }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);
  const { t } = useWebTranslation();

  const syncEdges = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  const disclaimer = t(DISCLAIMER_KEY);
  const showDisclaimer = isOrg("swe") && disclaimer && disclaimer !== DISCLAIMER_KEY;

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <TextFieldsV2 title={title} text={text} center gutterBottom={0} />
      </div>
      <div className={styles.carouselWrapper}>
        <Swiper
          slidesPerView="auto"
          spaceBetween={16}
          centeredSlides
          modules={[Mousewheel]}
          mousewheel={{ forceToAxis: true }}
          breakpoints={{
            500: { centeredSlides: false },
          }}
          className={styles.carousel}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            syncEdges(swiper);
          }}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.activeIndex);
            syncEdges(swiper);
          }}
        >
          {items.map((item) => (
            <SwiperSlide key={item.title} className={styles.slide}>
              <ReviewCard
                avatar={item.image ?? null}
                reviewText={item.text}
                authorName={item.title}
                authorSubtitle={item.description ?? null}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <CarouselControls
          count={items.length}
          activeIndex={activeIndex}
          onDotClick={(index) => swiperRef.current?.slideTo(index)}
          onPrev={() => swiperRef.current?.slidePrev()}
          onNext={() => swiperRef.current?.slideNext()}
          prevDisabled={isBeginning}
          nextDisabled={isEnd}
          className={classnames(styles.controls, {
            [styles.controlsDesktopHidden]: items.length <= 4,
          })}
        />
      </div>
      {showDisclaimer && <p className={styles.disclaimer}>{disclaimer}</p>}
    </section>
  );
};

export default ReviewsCarousel;
